import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import api from '../utils/api';
import { supabase } from '../webSupabaseClient';
import { 
    TruckIcon, 
    EyeIcon, 
    CheckCircleIcon, 
    ClockIcon,
    CurrencyDollarIcon,
    MapPinIcon,
    PhoneIcon,
    EnvelopeIcon,
    DocumentTextIcon,
    ArrowPathIcon
} from '@heroicons/react/24/outline';

function classNames(...classes) {
    return classes.filter(Boolean).join(' ');
}

const statusTranslations = {
    'created': 'Oluşturuldu',
    'draft': 'Taslak',
    'ready': 'İşleme Hazır',
    'processing': 'İşleniyor',
    'confirmed': 'Onaylandı',
    'shipped': 'Kargoya Verildi',
    'in_transit': 'Yolda',
    'delivered': 'Teslim Edildi',
    'cancelled': 'İptal Edildi',
    'shipping_failed': 'Kargo Hatası',
    'delivery_failed': 'Teslim Başarısız',
    'cancellation_requested': 'İptal Talebi',
    'lost': 'Kayıp',
    'returned': 'İade Edildi'
};

const statusColors = {
    'created': 'bg-blue-100 text-blue-800',
    'draft': 'bg-gray-100 text-gray-800',
    'ready': 'bg-cyan-100 text-cyan-800',
    'processing': 'bg-blue-200 text-blue-900',
    'confirmed': 'bg-green-100 text-green-800',
    'shipped': 'bg-purple-100 text-purple-800',
    'in_transit': 'bg-yellow-100 text-yellow-800',
    'delivered': 'bg-emerald-100 text-emerald-800',
    'cancelled': 'bg-red-100 text-red-800',
    'shipping_failed': 'bg-red-200 text-red-900',
    'delivery_failed': 'bg-orange-200 text-orange-900',
    'cancellation_requested': 'bg-yellow-200 text-yellow-900',
    'lost': 'bg-gray-200 text-gray-900',
    'returned': 'bg-orange-100 text-orange-800'
};

// Kargo Detay Modalı
const ShipmentDetailsModal = ({ shipment, closeModal, onUpdateStatus, onCreateShipment }) => {
    const [priceComparison, setPriceComparison] = useState(null);
    const [loading, setLoading] = useState(false);

    const fetchPriceComparison = async () => {
        // Fiyat karşılaştırma özelliği devre dışı. Sadece bilgilendirme yap.
        toast.info('Fiyat karşılaştırma özelliği devre dışı bırakıldı.');
        setPriceComparison(null);
    };

    useEffect(() => {
        // Fiyat karşılaştırma devre dışı, otomatik çağrı yapılmıyor
    }, [shipment.kargonomi_shipment_id]);

    const handleConfirmShipment = async (shippingProviderId = -1) => {
        setLoading(true);
        try {
            // Doğru endpoint: /api/shipments/admin/:id/confirm
            // confirm işlemi varsa yalnızca kargonomide halihazırda seçilen/otomatik sağlayıcı için kullanılabilir.
            // Bu panelde fiyat karşılaştırması kapalı olduğundan, burada başka bir çağrı yapmıyoruz.
            const response = await api.post(`/api/shipments/admin/${shipment.id}/confirm`, {
                shippingProviderId
            });
            
            console.log('Kargo onaylama yanıtı:', response);
            toast.success('Kargo başarıyla onaylandı!');
            onUpdateStatus();
            closeModal();
        } catch (error) {
            console.error('Kargo onaylama hatası:', error);
            toast.error('Kargo onaylanamadı: ' + (error.response?.data?.error || error.message));
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return dateString ? new Date(dateString).toLocaleDateString('tr-TR') : '-';
    };

    return (
        <div className="p-4 max-w-4xl w-full bg-white rounded-lg">
            <div className="flex justify-between items-start mb-6">
                <div>
                    <h3 className="text-2xl font-bold text-gray-800">
                        Kargo Detayları - Sipariş #{shipment.orders?.order_number}
                    </h3>
                    <div className="flex items-center mt-2">
                        <span className={`px-3 py-1 text-sm font-semibold rounded-full ${statusColors[shipment.status]}`}>
                            {statusTranslations[shipment.status]}
                        </span>
                        {(shipment.tracking_code || shipment.tracking_number) && (
                            <span className="ml-3 text-sm text-gray-600">
                                Takip: <strong>{shipment.tracking_code || shipment.tracking_number}</strong>
                            </span>
                        )}
                    </div>
                </div>
                <button
                    onClick={() => onUpdateStatus(shipment.orders.id)}
                    className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg"
                    title="Durumu Güncelle"
                >
                    <ArrowPathIcon className="h-5 w-5" />
                </button>
            </div>

            {/* Sipariş Bilgileri */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                        <DocumentTextIcon className="h-5 w-5 mr-2" />
                        Sipariş Bilgileri
                    </h4>
                    <div className="space-y-2 text-sm">
                        <div><span className="font-medium">Sipariş No:</span> {shipment.orders?.order_number}</div>
                        <div><span className="font-medium">Müşteri:</span> {shipment.orders?.customer_name}</div>
                        <div><span className="font-medium">E-posta:</span> {shipment.orders?.customer_email || shipment.orders?.guest_email}</div>
                        <div><span className="font-medium">Toplam Tutar:</span> ₺{shipment.orders?.total_price}</div>
                        <div><span className="font-medium">Oluşturma:</span> {formatDate(shipment.created_at)}</div>
                    </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                        <MapPinIcon className="h-5 w-5 mr-2" />
                        Teslimat Adresi
                    </h4>
                    {shipment.orders?.shipping_address && (
                        <div className="space-y-2 text-sm">
                            {(() => {
                                let address = shipment.orders.shipping_address;
                                
                                // Güvenli JSON parse
                                if (typeof address === 'string') {
                                    try {
                                        address = JSON.parse(address);
                                    } catch (e) {
                                        console.warn('Adres parse hatası:', e, address);
                                        return <div className="text-red-500">Adres formatı hatalı</div>;
                                    }
                                }
                                
                                return (
                                    <>
                                        <div><span className="font-medium">Alıcı:</span> {address.full_name || 'Bilinmeyen'}</div>
                                        <div><span className="font-medium">Adres:</span> {address.address_line || 'Bilinmeyen'}</div>
                                        <div><span className="font-medium">Şehir/İlçe:</span> {address.city || 'Bilinmeyen'}, {address.district || 'Bilinmeyen'}</div>
                                        <div><span className="font-medium">Telefon:</span> {address.phone || 'Bilinmeyen'}</div>
                                    </>
                                );
                            })()}
                        </div>
                    )}
                </div>
            </div>

            {/* Kargo Bilgileri */}
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                    <TruckIcon className="h-5 w-5 mr-2" />
                    Kargo Bilgileri
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div><span className="font-medium">Kargo Firması:</span> {shipment.shipping_provider || 'Henüz seçilmedi'}</div>
                    <div><span className="font-medium">Kargo Ücreti:</span> ₺{shipment.shipping_cost || '0'}</div>
                        <div className="space-y-1">
                            <div><span className="font-medium">Ref:</span> {shipment.reference_number || '-'}</div>
                            <div className="text-xs text-gray-600"><span className="font-medium">ID:</span> {shipment.kargonomi_shipment_id || '-'}</div>
                        </div>
                </div>
            </div>

            {/* Fiyat Karşılaştırması bölümü devre dışı bırakıldı */}
            {/* Bu alan kasıtlı olarak kaldırıldı. */}

            {/* Kargo Oluşturma Butonu */}
            {!shipment.kargonomi_shipment_id && (
                <div className="text-center">
                    <button
                        onClick={() => onCreateShipment(shipment.order_id)}
                        className="bg-blue-600 text-white py-2 px-6 rounded-lg hover:bg-blue-700"
                        disabled={loading || !shipment.order_id}
                    >
                        Kargo Oluştur
                    </button>
                    {!shipment.order_id && (
                        <p className="text-xs text-red-600 mt-1">Sipariş ID bulunamadı</p>
                    )}
                </div>
            )}

            <div className="mt-6 pt-4 border-t flex justify-end">
                <button
                    onClick={closeModal}
                    className="bg-gray-200 hover:bg-gray-300 py-2 px-4 rounded-md text-sm font-semibold"
                >
                    Kapat
                </button>
            </div>
        </div>
    );
};

function ShippingManagementPanel({ openModal, closeModal }) {
    const { token } = useAuth();
    const [shipments, setShipments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('');

    const fetchShipments = useCallback(async () => {
        if (!token) return;

        setLoading(true);
        try {
            const params = statusFilter ? { status: statusFilter } : {};
            const headers = {
                'Authorization': `Bearer ${token}`
            };
            const response = await api.get('/api/shipping/list', { params, headers });
            console.log('Kargo listesi API yanıtı:', response);
            
            // API yanıtının array olup olmadığını kontrol et
            if (Array.isArray(response)) {
                setShipments(response);
            } else if (response && response.success && Array.isArray(response.data)) {
                setShipments(response.data);
            } else if (response && Array.isArray(response.data)) {
                setShipments(response.data);
            } else {
                console.warn('Beklenmeyen API yanıt formatı:', response);
                setShipments([]);
            }
        } catch (error) {
            console.error('Kargo listesi alınamadı:', error);
            toast.error('Kargo listesi alınamadı');
        } finally {
            setLoading(false);
        }
    }, [token, statusFilter]);

    useEffect(() => {
        fetchShipments();
    }, [fetchShipments]);

    const handleCreateShipment = async (orderId) => {
        if (!orderId || orderId === 'undefined') {
            toast.error('Geçersiz sipariş ID\'si');
            return;
        }
        
        try {
            const headers = {
                'Authorization': `Bearer ${token}`
            };
            await api.post(`/api/orders/${orderId}/auto-shipping`, {}, { headers });
            toast.success('Kargo başarıyla oluşturuldu ve takip numarası alındı');
            fetchShipments();
            closeModal();
        } catch (error) {
            console.error('Kargo oluşturma hatası:', error);
            toast.error('Kargo oluşturulurken bir hata oluştu: ' + error.message);
        }
    };

    const handleUpdateStatus = async (orderId) => {
        if (!orderId || orderId === 'undefined') {
            toast.error('Geçersiz sipariş ID\'si');
            return;
        }
        
        try {
            const headers = {
                'Authorization': `Bearer ${token}`
            };
            await api.post(`/api/orders/${orderId}/update-shipping-status`, {}, { headers });
            toast.success('Kargo durumu güncellendi');
            fetchShipments();
        } catch (error) {
            console.error('Durum güncelleme hatası:', error);
            toast.error('Kargo durumu güncellenemedi: ' + error.message);
        }
    };

    const openShipmentDetails = (shipment) => {
        openModal(
            <ShipmentDetailsModal
                shipment={shipment}
                closeModal={closeModal}
                onUpdateStatus={handleUpdateStatus}
                onCreateShipment={handleCreateShipment}
            />
        );
    };

    const formatDate = (dateString) => {
        return dateString ? new Date(dateString).toLocaleDateString('tr-TR') : '-';
    };

    return (
        <div className="container mx-auto p-2 sm:p-4">
            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
                <div className="bg-gradient-to-r from-brand-green to-brand-green-light p-4 sm:p-6 text-white">
                    <h2 className="text-xl sm:text-2xl font-bold mb-1 sm:mb-2">Kargo Yönetimi</h2>
                    <p className="opacity-90 text-sm sm:text-base">Kargo gönderilerini yönetin ve takip edin</p>
                </div>

                <div className="p-4 sm:p-6">
                    {/* Filtreler */}
                    <div className="mb-4 flex flex-wrap gap-2">
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="px-3 py-2 border rounded-lg focus:ring-brand-green focus:border-brand-green"
                        >
                            <option value="">Tüm Durumlar</option>
                            {Object.entries(statusTranslations).map(([key, value]) => (
                                <option key={key} value={key}>{value}</option>
                            ))}
                        </select>
                        <button
                            onClick={fetchShipments}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                            Yenile
                        </button>
                    </div>

                    {loading ? (
                        <p className="text-center py-8">Kargolar yükleniyor...</p>
                    ) : (
                        <>
                            {/* Desktop Tablo */}
                            <div className="hidden lg:block overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sipariş</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Alıcı</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kargo Firması</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Takip No</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Durum</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tarih</th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">İşlemler</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {shipments.map((shipment) => (
                                            <tr key={shipment.id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        #{shipment.orders?.order_number}
                                                    </div>
                                                    <div className="text-sm text-gray-500">
                                                        ₺{shipment.orders?.total_price}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-900">
                                                        {shipment.orders?.customer_name}
                                                    </div>
                                                    <div className="text-sm text-gray-500">
                                                        {(() => {
                                                            if (!shipment.orders?.shipping_address) return 'Bilinmeyen';
                                                            
                                                            let address = shipment.orders.shipping_address;
                                                            if (typeof address === 'string') {
                                                                try {
                                                                    address = JSON.parse(address);
                                                                } catch (e) {
                                                                    return 'Adres hatası';
                                                                }
                                                            }
                                                            return address.city || 'Bilinmeyen';
                                                        })()}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {shipment.shipping_provider || 'Henüz seçilmedi'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    <div className="flex flex-col">
                                                        <span><span className="font-medium">Kargo:</span> {shipment.shipping_provider || 'Kargo firması bekleniyor'}</span>
                                                        <span className="text-xs text-gray-700"><span className="font-medium">Kargonomi id:</span> {shipment.kargonomi_shipment_id || 'ID bekleniyor'}</span>
                                                        <span className="text-xs text-gray-700"><span className="font-medium">Referans numarası:</span> {shipment.reference_number || 'Referans numarası bekleniyor'}</span>
                                                        <span className="text-xs text-gray-700"><span className="font-medium">Takip numarası:</span> {shipment.tracking_code || shipment.tracking_number || 'Takip numarası bekleniyor'}</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${statusColors[shipment.status]}`}>
                                                        {statusTranslations[shipment.status]}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {formatDate(shipment.created_at)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                    <button
                                                        onClick={() => openShipmentDetails(shipment)}
                                                        className="text-indigo-600 hover:text-indigo-900 p-1"
                                                        title="Detayları Görüntüle"
                                                    >
                                                        <EyeIcon className="h-5 w-5" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobil Kart Görünümü */}
                            <div className="lg:hidden space-y-4">
                                {shipments.map((shipment) => (
                                    <div key={shipment.id} className="bg-white rounded-lg shadow border border-gray-200 p-4">
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="flex-1">
                                                <h3 className="text-sm font-semibold text-gray-900">
                                                    Sipariş #{shipment.orders?.order_number}
                                                </h3>
                                                <p className="text-xs text-gray-500 mt-1">
                                                    {shipment.orders?.customer_name}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    {formatDate(shipment.created_at)}
                                                </p>
                                            </div>
                                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${statusColors[shipment.status]}`}>
                                                {statusTranslations[shipment.status]}
                                            </span>
                                        </div>
                                        
                                        <div className="mb-3 text-sm">
                                            <div><span className="font-medium">Kargo:</span> {shipment.shipping_provider || 'Kargo firması bekleniyor'}</div>
                                            <div><span className="font-medium">Kargonomi id:</span> {shipment.kargonomi_shipment_id || 'ID bekleniyor'}</div>
                                            <div><span className="font-medium">Referans numarası:</span> {shipment.reference_number || 'Referans numarası bekleniyor'}</div>
                                            <div><span className="font-medium">Takip numarası:</span> {shipment.tracking_code || shipment.tracking_number || 'Takip numarası bekleniyor'}</div>
                                        </div>
                                        
                                        <div className="flex justify-end">
                                            <button
                                                onClick={() => openShipmentDetails(shipment)}
                                                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-sm"
                                            >
                                                Detaylar
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {shipments.length === 0 && (
                                <p className="text-center text-gray-500 mt-8">Henüz kargo kaydı bulunmuyor.</p>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ShippingManagementPanel;
